import { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../layouts/AdminLayout";
import Button from "../../components/ui/Button";
import LogoutConfirmModal from "../../components/auth/LogoutConfirmModal";
import StatusBadge from "../../components/ui/StatusBadge";
import Skeleton from "../../components/ui/Skeleton";
import { EmptyState, ErrorCard } from "../../components/ui/FeedbackStates";
import { getStoredAuthUser } from "../../services/authService";
import { getMyProfile } from "../../services/userService";
import { getMyAuditLogs } from "../../services/systemLogsService";
import { LogOut, User, Shield, Home, Users, Car, Activity } from "lucide-react";
import styles from "./Profile.module.css";

const Field = ({ label, value }) => (
  <div className={styles.field}>
    <div className={styles.label}>{label}</div>
    <div className={styles.value}>{value || "-"}</div>
  </div>
);

const Profile = () => {
  const authUser = getStoredAuthUser();
  const [profile, setProfile] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLimit] = useState(20);
  const [auditOffset, setAuditOffset] = useState(0);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const [profileRes, auditRes] = await Promise.all([
          getMyProfile(),
          getMyAuditLogs({ limit: auditLimit, offset: 0 }),
        ]);
        if (!mounted) return;
        setProfile(profileRes);
        setAudit(auditRes);
        setAuditOffset(0);
        setError("");
      } catch (e) {
        console.error(e);
        if (!mounted) return;
        setError("Unable to load profile information right now.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [auditLimit]);

  const loadAuditPage = async (nextOffset) => {
    setAuditLoading(true);
    try {
      const auditRes = await getMyAuditLogs({ limit: auditLimit, offset: nextOffset });
      setAudit(auditRes);
      setAuditOffset(nextOffset);
    } catch (e) {
      console.error(e);
    } finally {
      setAuditLoading(false);
    }
  };

  const user = profile?.user || authUser;
  const societyName = profile?.society?.society_name || (user?.society_id ? `Society ${user.society_id}` : "-");
  const familyMembers = useMemo(() => profile?.family_members || [], [profile]);
  const vehicles = useMemo(() => profile?.vehicles || [], [profile]);
  const flatMappings = useMemo(() => profile?.flat_mappings || [], [profile]);
  const logs = useMemo(() => audit?.logs || [], [audit]);
  const totalLogs = Number(audit?.total ?? audit?.count ?? 0) || 0;
  const pageStart = totalLogs ? auditOffset + 1 : 0;
  const pageEnd = totalLogs ? Math.min(auditOffset + logs.length, totalLogs) : 0;

  return (
    <AdminLayout title="My Profile">
      <div className={styles.page}>
        <div className={styles.header}>
          <h1 className={styles.title} style={{ fontSize: "22px", fontWeight: 800 }}>Account Profile</h1>
          <p className={styles.subTitle}>Manage and verify your identity, assignments, and account security.</p>
        </div>

        <div className={[styles.card, styles.heroCard].join(" ")} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ width: "54px", height: "54px", borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <User size={28} color="#ffffff" />
            </div>
            <div>
              <div className={styles.heroName} style={{ fontSize: "18px" }}>{user?.full_name || user?.name || "User"}</div>
              <div className={styles.heroRole} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Shield size={13} />
                <span>{user?.system_role || user?.primary_role || "User"}</span>
                {user?.is_active !== false && <span style={{ opacity: 0.8 }}>• Active</span>}
              </div>
            </div>
          </div>
          <Button variant="danger" size="sm" onClick={() => setShowLogoutModal(true)}>
            <LogOut size={15} style={{ marginRight: "6px" }} /> Sign Out
          </Button>
        </div>

        {loading ? (
          <div className={styles.card}>
            <Skeleton variant="card" count={3} />
          </div>
        ) : error ? (
          <ErrorCard title="Profile Error" message={error} />
        ) : (
          <>
            <div className={styles.card}>
              <h3 className={styles.sectionTitle} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "15px", fontWeight: 700, margin: "0 0 16px 0" }}>
                <User size={16} color="var(--color-primary)" /> Personal & Account Information
              </h3>
              <div className={styles.grid}>
                <Field label="User ID" value={`#${user?.user_id || "-"}`} />
                <Field label="Email ID" value={user?.email_id || user?.email} />
                <Field label="Mobile Number" value={user?.mobile_number ? `+91 ${user.mobile_number}` : "-"} />
                <Field label="Property / Society" value={societyName} />
                <Field label="Account Type" value={user?.account_type} />
                <Field label="Platform OS" value={user?.os_type} />
                <Field label="Gender" value={user?.gender} />
                <div className={styles.field}>
                  <div className={styles.label}>Account Status</div>
                  <div style={{ marginTop: "4px" }}>
                    <StatusBadge
                      status={user?.is_active === false ? "INACTIVE" : "ACTIVE"}
                      label={user?.is_active === false ? "Inactive" : "Active"}
                    />
                  </div>
                </div>
                <Field
                  label="Registered On"
                  value={user?.created_at ? new Date(user.created_at).toLocaleString() : "-"}
                />
                <Field
                  label="Last Updated"
                  value={user?.updated_at ? new Date(user.updated_at).toLocaleString() : "-"}
                />
              </div>
            </div>

            <div className={styles.card}>
              <h3 className={styles.sectionTitle} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "15px", fontWeight: 700, margin: "0 0 16px 0" }}>
                <Home size={16} color="var(--color-primary)" /> Unit & Flat Allocations
              </h3>
              {flatMappings.length === 0 ? (
                <EmptyState
                  title="No assigned units"
                  description="You do not currently have any flats, desks, or retail units associated with this account."
                />
              ) : (
                <div className={styles.list}>
                  {flatMappings.map((m) => (
                    <div key={m.mapping_id} className={styles.row}>
                      <div>
                        <div className={styles.value} style={{ fontWeight: 600 }}>
                          {m?.flats?.flat_number ? `Unit ${m.flats.flat_number}` : `Flat ID ${m.flat_id}`}
                        </div>
                        <div className={styles.muted} style={{ fontSize: "12px", marginTop: "2px" }}>
                          {m?.flats?.unit_type ? `Type: ${m.flats.unit_type}` : ""}
                          {m?.flats?.status ? ` • Status: ${m.flats.status}` : ""}
                        </div>
                      </div>
                      <StatusBadge status="ACTIVE" label={m.ownership_type || "Occupant"} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3 className={styles.sectionTitle} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "15px", fontWeight: 700, margin: "0 0 16px 0" }}>
                <Users size={16} color="var(--color-primary)" /> Registered Family Members
              </h3>
              {familyMembers.length === 0 ? (
                <EmptyState
                  title="No family members"
                  description="No secondary family members or associates registered under this profile."
                />
              ) : (
                <div className={styles.list}>
                  {familyMembers.map((m) => (
                    <div key={m.family_member_id} className={styles.row}>
                      <div>
                        <div className={styles.value} style={{ fontWeight: 600 }}>{m.member_name}</div>
                        <div className={styles.muted} style={{ fontSize: "12px", marginTop: "2px" }}>
                          {m.relation ? `Relation: ${m.relation}` : "Relation: -"} • {m.member_type || "Family"}
                        </div>
                      </div>
                      <div className={styles.muted} style={{ fontSize: "12px" }}>
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3 className={styles.sectionTitle} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "15px", fontWeight: 700, margin: "0 0 16px 0" }}>
                <Car size={16} color="var(--color-primary)" /> Authorized Vehicles
              </h3>
              {vehicles.length === 0 ? (
                <EmptyState
                  title="No vehicles registered"
                  description="No license plates registered for automated gate barrier pass."
                />
              ) : (
                <div className={styles.list}>
                  {vehicles.map((v) => (
                    <div key={v.vehicle_id} className={styles.row}>
                      <div>
                        <div className={styles.value} style={{ fontWeight: 700, color: "#d97706" }}>
                          {v.vehicle_number}
                        </div>
                        <div className={styles.muted} style={{ fontSize: "12px", marginTop: "2px" }}>
                          {v.vehicle_type}
                          {v.model ? ` • ${v.model}` : ""}
                          {v.color ? ` • ${v.color}` : ""}
                          {v.parking_slot ? ` • Assigned Slot: ${v.parking_slot}` : ""}
                        </div>
                      </div>
                      <div className={styles.muted} style={{ fontSize: "12px" }}>
                        {v.created_at ? new Date(v.created_at).toLocaleDateString() : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.card}>
              <h3 className={styles.sectionTitle} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "15px", fontWeight: 700, margin: "0 0 16px 0" }}>
                <Activity size={16} color="var(--color-primary)" /> Personal Audit Activity
              </h3>
              {logs.length === 0 ? (
                <EmptyState
                  title="No activity records"
                  description="Your recent security and authentication events will appear here."
                />
              ) : (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table className={styles.table}>
                      <thead className={styles.thead}>
                        <tr>
                          <th>Timestamp</th>
                          <th>Module</th>
                          <th>Action</th>
                          <th>Status</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody className={styles.tbody}>
                        {logs.map((l) => (
                          <tr key={l.id}>
                            <td className={styles.code}>
                              {l.created_at ? new Date(l.created_at).toLocaleString() : "-"}
                            </td>
                            <td className={styles.code}>{l.module}</td>
                            <td className={styles.code}>{l.action}</td>
                            <td>
                              <StatusBadge status={l.status} />
                            </td>
                            <td>{l.description || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className={styles.pager} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
                    <div className={styles.pagerMeta} style={{ fontSize: "13px", color: "var(--color-text-muted)" }}>
                      {totalLogs ? `Showing ${pageStart}-${pageEnd} of ${totalLogs}` : `Showing ${logs.length}`}
                    </div>
                    <div className={styles.pagerBtns} style={{ display: "flex", gap: "8px" }}>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={auditLoading || auditOffset <= 0}
                        onClick={() => loadAuditPage(Math.max(0, auditOffset - auditLimit))}
                      >
                        Previous
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={auditLoading || auditOffset + auditLimit >= totalLogs}
                        onClick={() => loadAuditPage(auditOffset + auditLimit)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      <LogoutConfirmModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
      />
    </AdminLayout>
  );
};

export default Profile;
